import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Euro, ShoppingBag, TrendingUp, Users } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const OrderStats = ({ orders }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const totalRevenue = orders.reduce(
    (sum, order) => sum + parseFloat(order.total_amount || 0),
    0,
  );
  const totalOrders = orders.length;
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const uniqueCustomers = new Set(
    orders.map((o) => o.customer_details?.email).filter(Boolean),
  ).size;

  const ordersByDate = orders.reduce((acc, order) => {
    const date = new Date(order.created_at).toLocaleDateString("nl-BE", {
      month: "short",
      day: "numeric",
    });
    acc[date] = (acc[date] || 0) + parseFloat(order.total_amount || 0);
    return acc;
  }, {});

  const lineChartData = Object.entries(ordersByDate)
    .map(([date, amount]) => ({ date, amount }))
    .slice(-7);

  const statusDistribution = orders.reduce((acc, order) => {
    const status = order.status || "unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = Object.entries(statusDistribution).map(
    ([name, value]) => ({ name, value }),
  );

  const COLORS = {
    new: "#6366f1",
    handled: "#10b981",
    paid: "#22c55e",
    pending: "#eab308",
    shipped: "#3b82f6",
    cancelled: "#ef4444",
    unknown: "#94a3b8",
  };

  const stats = [
    {
      label: "Totale Omzet",
      value: `€ ${totalRevenue.toFixed(2)}`,
      sub: "Lifetime revenue",
      icon: Euro,
    },
    {
      label: "Bestellingen",
      value: totalOrders,
      sub: "Totaal aantal",
      icon: ShoppingBag,
    },
    {
      label: "Gem. Orderwaarde",
      value: `€ ${averageOrderValue.toFixed(2)}`,
      sub: "Per bestelling",
      icon: TrendingUp,
    },
    {
      label: "Klanten",
      value: uniqueCustomers,
      sub: "Unieke klanten",
      icon: Users,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                {stat.label}
              </span>
              <stat.icon
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-300"}`}
              />
            </div>
            <p
              className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {stat.value}
            </p>
            <span
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              {stat.sub}
            </span>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-3">
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <h3
            className={`text-[13px] font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Omzet Verloop
          </h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={isDark ? "rgba(255,255,255,0.04)" : "#f0f0f0"}
                />
                <XAxis
                  dataKey="date"
                  stroke={isDark ? "#555" : "#aaa"}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke={isDark ? "#555" : "#aaa"}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `€${v}`}
                />
                <Tooltip
                  formatter={(value) => [`€ ${value}`, "Omzet"]}
                  contentStyle={{
                    borderRadius: "8px",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid #e5e7eb",
                    backgroundColor: isDark ? "#1a1a1a" : "#fff",
                    color: isDark ? "#fff" : "#111",
                    fontSize: "12px",
                    boxShadow: "none",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke={isDark ? "#fff" : "#111"}
                  strokeWidth={2}
                  dot={{ r: 3, fill: isDark ? "#fff" : "#111" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <h3
            className={`text-[13px] font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Order Status
          </h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={75}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[entry.name] || COLORS.unknown}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid #e5e7eb",
                    backgroundColor: isDark ? "#1a1a1a" : "#fff",
                    color: isDark ? "#fff" : "#111",
                    fontSize: "12px",
                    boxShadow: "none",
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{
                    fontSize: "11px",
                    color: isDark ? "#888" : "#666",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderStats;
